import { useSessionStore } from "../../../Stores/Session";
import { retryDelayFor, shouldRetryRequest } from "./retryPolicy";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface RequestConfig {
  method?: HttpMethod;
  url: string;
  data?: any;
  headers?: Record<string, string | boolean>;
  params?: Record<string, any>;
  timeout?: number;
  // Opt OUT of the automatic retries for requests that are not safe to repeat.
  // A timed-out POST may well have been processed by the server (the abort only
  // stops us waiting for the response), so retrying it creates a second record.
  retry?: boolean;
  _retryCount?: number;
}

const DEFAULT_TIMEOUT = 4000;

interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export class HttpClientXHR {
  public static baseURL: string;
  private defaultHeaders: Record<string, string | boolean>;

  constructor() {
    const { apiUrl } = useSessionStore.getState();
    HttpClientXHR.baseURL = apiUrl || "https://dashapi.gleap.io";
    this.defaultHeaders = {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "Access-Control-Allow-Credentials": true,
    };
  }

  public static setBaseURL(url: string) {
    HttpClientXHR.baseURL = url;
  }

  private buildURL(url: string, params?: Record<string, any>): string {
    const fullUrl = `${HttpClientXHR.baseURL}${url}`;
    if (params) {
      const query = Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&");
      return fullUrl + (fullUrl.includes("?") ? "&" : "?") + query;
    }
    return fullUrl;
  }

  // Manually inject tokens and other custom headers into every request.
  protected injectToken(
    headers: Record<string, string | boolean>
  ): Record<string, string | boolean> {
    const { session, sdkKey } = useSessionStore.getState();

    if (session?.fileAccessToken) headers["X-File-Session"] = session.fileAccessToken;

    if (sdkKey) {
      headers["api-token"] = sdkKey;
    }

    if (session && session.gleapId) {
      headers["gleap-id"] = session.gleapId;
    }

    if (session && session.gleapHash) {
      headers["gleap-hash"] = session.gleapHash;
    }

    return headers;
  }

  private async makeRequest<T = any>(
    config: RequestConfig
  ): Promise<HttpResponse<T>> {
    return new Promise<HttpResponse<T>>((resolve, reject) => {
      const { method = "GET", url, data, headers, params } = config;
      const xhr = new XMLHttpRequest();
      const fullUrl = this.buildURL(url, params);

      xhr.open(method, fullUrl, true);
      xhr.timeout = config.timeout ?? DEFAULT_TIMEOUT;

      // Merge default headers and custom headers then inject token info
      let allHeaders: Record<string, string | boolean> = {
        ...this.defaultHeaders,
        ...headers,
      };
      allHeaders = this.injectToken(allHeaders);

      Object.keys(allHeaders).forEach((key) => {
        xhr.setRequestHeader(key, String(allHeaders[key]));
      });

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XMLHttpRequest.DONE) {
          let responseData;
          try {
            responseData = JSON.parse(xhr.responseText);
          } catch (e) {
            responseData = xhr.responseText;
          }

          // Parse response headers
          const responseHeaders: Record<string, string> = {};
          const headerString = xhr.getAllResponseHeaders();
          if (headerString) {
            headerString.split("\r\n").forEach((line) => {
              const parts = line.split(": ");
              if (parts.length === 2) {
                responseHeaders[parts[0].toLowerCase()] = parts[1];
              }
            });
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({
              data: responseData,
              status: xhr.status,
              statusText: xhr.statusText,
              headers: responseHeaders,
            });
          } else {
            reject({
              status: xhr.status,
              statusText: xhr.statusText,
              data: responseData,
              headers: responseHeaders,
            });
          }
        }
      };

      xhr.onerror = () => {
        reject({
          status: 0,
          statusText: "Network Error",
          data: null,
          headers: {},
        });
      };

      // Stalled connection: the server never responded within the timeout. Reject with
      // status 0 so the existing retryCondition (`!error.status`) picks it up and retries.
      xhr.ontimeout = () => {
        reject({
          status: 0,
          statusText: "Timeout",
          data: null,
          headers: {},
        });
      };

      if (data) {
        xhr.send(JSON.stringify(data));
      } else {
        xhr.send();
      }
    });
  }

  async request<T = any>(config: RequestConfig): Promise<HttpResponse<T>> {
    const retryCount = config._retryCount || 0;

    try {
      return await this.makeRequest<T>(config);
    } catch (error) {
      if (
        !shouldRetryRequest({
          status: (error as any)?.status,
          retryCount,
          retry: config.retry,
        })
      ) {
        throw error;
      }

      await sleep(retryDelayFor(retryCount));

      // Retry the request
      return this.request<T>({ ...config, _retryCount: retryCount + 1 });
    }
  }

  get<T = any>(
    url: string,
    config?: Omit<RequestConfig, "method" | "url">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "GET", url });
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "method" | "url" | "data">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "POST", url, data });
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig, "method" | "url" | "data">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "PUT", url, data });
  }

  delete<T = any>(
    url: string,
    config?: Omit<RequestConfig, "method" | "url">
  ): Promise<HttpResponse<T>> {
    return this.request<T>({ ...config, method: "DELETE", url });
  }
}
